import { useState } from 'react'
import usePersistedFilters from '../../hooks/usePersistedFilters'
import useFetchData from '../../hooks/UseFetchData'
import Table from '../../components/his/invoices/Table'
import PageHeader from '../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { ColumnGroup } from 'primereact/columngroup'
import { Row as ColumnRow } from 'primereact/row'

import { transformResult } from '../../utils/GroupByItem'
import CustomButton from '../../shared/CustomButton'
import * as XLSX from 'xlsx'
import { formatDateTime } from '../../utils/FormateDateTime'
import Filters from '../../components/his/doctorSummaryReport/Filters'
import { Column } from 'primereact/column'
import { AmiriRegBase64 } from '../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../assets/fonts/AmiriB'

const getTotalPatients = row =>
  row?.total_patients ??
  row?.total_patient ??
  row?.totalPatients ??
  row?.TotalPatients ??
  row?.patients ??
  row?.patient_count ??
  0

const columns = t => [
  {
    header: t('Practitioner'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>{row?.name}</span>
    ),
  },
  {
    header: t('Department'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {row?._id?.department_en}
      </span>
    ),
  },
  {
    header: t('Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {getTotalPatients(row)}
      </span>
    ),
  },
  {
    header: t('Patient Share'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.patient_share_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Patient VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.patient_vat_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Patient Total'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.patient_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Company Share'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.company_share_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Company VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.company_vat_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Company Total'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.company_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Total Discount'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.total_discount || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Total VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.total_vat || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Cash'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.cash_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Card'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.card_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Bank'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.bank_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Cheque'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.cheque_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Multi Payment'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.multi_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Deposit Total'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.deposit_total || 0).toFixed(2)}
      </span>
    ),
  },
  {
    header: t('Overall Total'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.gross ? 'font-bold' : ''}>
        {Number(row?.overall_total || 0).toFixed(2)}
      </span>
    ),
  },
]

export const exportInvoicesToExcel = (
  data,
  t,
  fileName = 'doctor-summary-report.xlsx'
) => {
  if (!data || data.length === 0) return

  const formatted = data?.map(row => {
    return {
      [t('Practitioner')]: row?.name || '-',
      [t('Department')]: row?.gross ? '' : row?._id?.department_en || '-',
      [t('Total Patients')]: getTotalPatients(row),
      [t('Patient Share')]: Number(row?.patient_share_total || 0).toFixed(2),
      [t('Patient VAT')]: Number(row?.patient_vat_total || 0).toFixed(2),
      [t('Patient Total')]: Number(row?.patient_total || 0).toFixed(2),
      [t('Company Share')]: Number(row?.company_share_total || 0).toFixed(2),
      [t('Company VAT')]: Number(row?.company_vat_total || 0).toFixed(2),
      [t('Company Total')]: Number(row?.company_total || 0).toFixed(2),
      [t('Total Discount')]: Number(row?.total_discount || 0).toFixed(2),
      [t('Total VAT')]: Number(row?.total_vat || 0).toFixed(2),
      [t('Cash')]: Number(row?.cash_total || 0).toFixed(2),
      [t('Card')]: Number(row?.card_total || 0).toFixed(2),
      [t('Bank')]: Number(row?.bank_total || 0).toFixed(2),
      [t('Cheque')]: Number(row?.cheque_total || 0).toFixed(2),
      [t('Multi Payment')]: Number(row?.multi_total || 0).toFixed(2),
      [t('Deposit Total')]: Number(row?.deposit_total || 0).toFixed(2),
      [t('Overall Total')]: Number(row?.overall_total || 0).toFixed(2),
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(formatted)
  worksheet['!cols'] = new Array(Object.keys(formatted[0]).length).fill({
    wch: 20,
  })
  worksheet['!rows'] = new Array(formatted.length).fill({ hpt: 22 })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Doctor Summary Report')
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesToPDF = (
  payload,
  t,
  payloadData,
  fileName = 'user-collection-summary-report.pdf'
) => {
  const doc = new jsPDF('landscape')

  // Add Arabic font (font is added from Base64 string)
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64) // Embed the Base64-encoded font here
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal') // Register the font with jsPDF
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64) // Embed the Base64-encoded font here
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold') // Register the font with jsPDF
  doc.setFont('Amiri', 'normal') // Set the font to Amiri for Arabic text

  const lineHeight = 4
  const pageWidth = doc.internal.pageSize.getWidth()
  const currentDate = moment().format('dddd Do MMMM, YYYY')

  const start_date = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const end_date = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const user_name = payloadData?.user_name || 'All Users'

  const reportTitle = t('Doctor Summary Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || 'Organization'
  const location = payloadData?.address || 'Location'

  let y = 10

  doc.addImage(payloadData.logo, 'PNG', 10, 8, 25, 25)

  // Then shift the text to the right to make space for the logo
  const leftMargin = 40

  // Organization name
  doc.setFontSize(12)
  doc.text(orgName, leftMargin, y + 5)

  // Location
  doc.setFontSize(10)
  doc.text(location, leftMargin, y + lineHeight + 5)

  // Right-to-left (RTL) text for Arabic sections:
  doc.setFontSize(11)
  doc.text(reportTitle, pageWidth - 14, y, { align: 'right' })

  doc.setFontSize(10)
  doc.text(`Printed On: ${currentDate}`, pageWidth - 14, y + lineHeight, {
    align: 'right',
  })

  y += lineHeight * 2
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)
  doc.line(14, y, pageWidth - 14, y)
  y += 6

  const periodText = `${t('Daily collection summary for the period from')} ${start_date || '-'
    } ${t('and upto')} ${end_date || '-'}`
  doc.text(periodText, pageWidth - 14, y, { align: 'right' })

  // Add other text fields as Arabic
  doc.text(
    `${t('Report For')}: ${user_name || t('All Users')}`,
    pageWidth - 14,
    y + lineHeight,
    { align: 'right' }
  )

  // Define the main headers (Row 1)
  const tableHeaders = [
    [
      t('Practitioner'),
      t('Department'),
      t('Patients'),
      t('Patient'),
      '',
      '',
      t('Company'),
      '',
      '',
      t('Discount'),
      t('VAT'),
      t('Cash'),
      t('Card'),
      t('Bank'),
      t('Cheque'),
      t('Multi Payment'),
      t('Deposit Total'),
      t('Total'),
    ],

    ['', '', '', t('Share'), t('VAT'), t('Total'), t('Share'), t('VAT'), t('Total'), '', '', '', '', '', '', '', '', ''],
  ]

  const tableData = []

  // Handle flat array data from transformResult
  payload.forEach(row => {
    const isGross = !!row?.gross

    tableData.push([
      {
        content: row?.name || '-',
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: row?._id?.department_en || '-',
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: getTotalPatients(row),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.patient_share_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.patient_vat_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.patient_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.company_share_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.company_vat_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.company_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.total_discount || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.total_vat || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.cash_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.card_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.bank_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.cheque_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.multi_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.deposit_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
      {
        content: Number(row?.overall_total || 0).toFixed(2),
        styles: {
          font: 'Amiri',
          fontStyle: isGross ? 'bold' : 'normal',
        },
      },
    ])
  })

  // tableData.push([
  //   {
  //     content: 'Net Total',
  //     styles: { font: 'Amiri', fontStyle: !!footer.grand ?'bold':'normal', fontSize: 12 },
  //   },
  // ])

  let spanNext = false
  let spanPaymentMethod = false
  let span2Total = 0
  // let spanSubtotal = false

  // Render the table
  autoTable(doc, {
    startY: y + lineHeight * 3,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: 0,
      fontStyle: 'bold',
      halign: 'left',
      font: 'Amiri',
      fontSize: 8,
      lineWidth: 0.05, // Line width for header grid lines
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [255, 255, 255], // Background color for body
      lineColor: [0, 0, 0], // Grid line color (black)
      lineWidth: 0.1, // Line width for grid lines
      font: 'Amiri',
      fontSize: 8,
    },
    willDrawCell: data => {
      const rowText = data.cell.text?.[0]
      if (rowText === t('Total') || rowText === t('Net Total')) {
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fillColor = [240, 240, 240]
      }
    },
    didParseCell: data => {
      if (data.section === 'head') {
        const rowIndex = data.row.index
        const columnIndex = data.column.index

        if (rowIndex === 0 && (columnIndex === 3 || columnIndex === 6)) {
          data.cell.colSpan = 3
        }
      }
    },
  })

  doc.save(fileName)
}

const DoctorSummaryReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()
  const auth = useSelector(state => state.auth)
  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'doctor_summary_report',
    {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      practitioner: '',
      department_id: '',
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/summary', {
      ...payload,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      type: 'practitioner',
    })
  }

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow style={{ alignItems: 'end', borderWidth: 1 }}>
        <Column header={t('Practitioner')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Department')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Patients')} style={{ paddingLeft: '0.5rem' }} />

        <Column header={t('Patient')} colSpan={3} align={'center'} />
        <Column header={t('Company')} colSpan={3} align={'center'} />

        <Column header={t('Discount')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Cash')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Card')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Bank')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Cheque')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Multi Payment')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Deposit')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />
      </ColumnRow>
      <ColumnRow>
        <Column colSpan={3} />
        <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />

        <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
        <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />
        <Column colSpan={9} />
      </ColumnRow>
    </ColumnGroup>
  )

  // const footerGroup = (
  //   <ColumnGroup>
  //     <ColumnRow style={{ alignItems: 'end', borderWidth: 1 }}>
  //       <Column
  //         header={`${t('Subtotal for')} ${payloadData?.created_by}`}
  //         rowSpan={2}
  //       />
  //       <Column header={t('Invoice No')} rowSpan={2} />
  //       <Column header={t('MRN')} rowSpan={2} />

  //       <Column header={t('Patient')} colSpan={4} align={'center'} />
  //       <Column header={t('Company')} colSpan={3} align={'center'} />
  //     </ColumnRow>
  //     <ColumnRow>
  //       <Column header={t('Name')} align={'center'} />
  //       <Column header={t('Share')} align={'center'} />
  //       <Column header={t('VAT')} align={'center'} />
  //       <Column header={t('Total')} align={'center'} />

  //       <Column header={t('Share')} align={'center'} />
  //       <Column header={t('VAT')} align={'center'} />
  //       <Column header={t('Total')} align={'center'} />
  //     </ColumnRow>
  //   </ColumnGroup>
  // )

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('Doctor Summary Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div>
        <div className="flex items-center w-full justify-between gap-2 mb-2">
          <Filters
            payloadData={payloadData}
            setPayloadData={setPayloadData}
            requestReport={requestReport}
          />
          <div class=" flex gap-2">
            <CustomButton
              //label={t('Export to Excel')}
              title={t('Export to Excel')}
              buttonType="success"
              icon={FaFileExcel}
              className="w-fit h-[35px]"
              onClick={() => {
                exportInvoicesToExcel(
                  transformResult(requestData?.grouped_reports, [
                    '_id',
                    'practitioner_name',
                  ]),
                  t
                )
              }}
            />

            <CustomButton
              //label={t('Export to PDF')}
              title={t('Export to PDF')}
              buttonType="danger"
              icon={FaFilePdf}
              className="w-fit h-[35px]"
              onClick={() => {
                exportInvoicesToPDF(
                  transformResult(requestData?.grouped_reports, [
                    '_id',
                    'practitioner_name',
                  ]),
                  t,
                  {
                    ...payloadData,
                    business_en: auth?.buisnessInfo?.name_en,
                    business_ar: auth?.buisnessInfo?.name_ar,
                    address: auth?.buisnessInfo?.address,
                    logo: auth?.buisnessInfo?.logo,
                  }
                )
              }}
            />
          </div>
        </div>

        <Table
          data={transformResult(requestData?.grouped_reports, [
            '_id',
            'practitioner_name',
          ])}
          loading={loading}
          columns={columns}
          headerColumnGroup={headerGroup}
          showGridlines
        />
      </div>
    </div>
  )
}

export default DoctorSummaryReport
